import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useInput, UseInput } from "hooks/useInput";
import { useAssets } from "hooks/useAssets";
import { useRequestDispatch } from "store/requestHooks";
import { useRootDispatch, useRootSelector } from "store/hooks";

import { InputDTO, Input } from "components/common/input/Input";
import { PaymentActionType } from "../AddPaymentMethods";
import { MakePaymentClick } from "components/sliderPage/makePayment/MakePayment";

import {
  extractError,
  getDefaultInputValue,
  renderConditionalProp,
} from "utility";
import { genericRegex } from "utility/regex";

import { updateSlide } from "store/common/common-slice";
import {
  selectAllPaymentMethods,
  selectPaymentMethodById,
} from "store/paymentMethods/paymentMethods-slice";
import {
  SavePaymentMethod,
  savePaymentMethod,
} from "store/paymentMethods/paymentMethods-action";
import { PaymentMethod } from "store/paymentMethods/paymentMethods-state";
import { useAddNotification } from "hooks";
import { selectedAccountInfo } from "store/account/account-selector";

import classes from "./AddAch.module.scss";
import { hasAccountPermission } from "utility/account";
import { AccountMatrix } from "store/account/account-state";

export interface AddAchProps {
  buttonClick?: MakePaymentClick;
  paymentHandler?: Function;
  action: PaymentActionType;
}

export const AddAch: React.FC<AddAchProps> = ({
  buttonClick,
  paymentHandler,
  action,
}) => {
  const { t } = useTranslation(["common", "payment", "payment_next"]);
  const { createAssetUrl } = useAssets();

  const [formState, setFormState] = useState<any>({});
  const accountInfo = useRootSelector(selectedAccountInfo);
  const [disabled, setDisabled] = useState(false);

  const { meta } = useRootSelector((state) => state.paymentMethods);
  const addNotification = useAddNotification();

  const existingPaymentMethods = useRootSelector((state) =>
    selectAllPaymentMethods(state),
  );

  const currentPaymentMethod = useRootSelector((state) => {
    const id = state.common.pageDetail?.data?.id;
    if (id) {
      return selectPaymentMethodById(state, id);
    }
    return null;
  });

  const accountType: UseInput = useInput(
    getDefaultInputValue(currentPaymentMethod?.bankAccountType, 1),
  );
  const ack: UseInput = useInput(
    getDefaultInputValue(
      action === PaymentActionType.PAY ? false : currentPaymentMethod?.ack,
    ),
  );
  const [isNickNameTaken, setIsNickNameTaken] = useState(false);

  const nickName: UseInput = useInput(
    getDefaultInputValue(currentPaymentMethod?.paymentMethodName, ""),
  );

  const handleNickOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    nickName.inputBlurHandler(e);
    const isNickNameDuplicate = existingPaymentMethods.some(
      (method) => method.paymentMethodName === e.target.value,
    );
    if (isNickNameDuplicate) {
      setIsNickNameTaken(true);
      addNotification({
        translatedMessages: [t("common:nickname_exists")],
        type: "ERROR",
        closable: true,
      });
      return;
    } else {
      setIsNickNameTaken(false);
    }
  };

  useEffect(() => {
    if (
      !hasAccountPermission(
        accountInfo?.clientAttributes,
        AccountMatrix.EnableACH,
      )
    ) {
      setDisabled(true);
    }
  }, [accountInfo]);

  const dispatch = useRootDispatch();
  const requestDispatch = useRequestDispatch();

  const formData: InputDTO[] = [
    {
      label: t("payment_next:name_of_bank"),
      key: "bankName",
      val: currentPaymentMethod?.bankName ? currentPaymentMethod?.bankName : "",
      validationMessage: t("common:field_not_empty"),
      extraProp: {
        required: true,
        maxLength: 50,
        pattern: genericRegex,
      },
    },
    {
      label: t("payment_next:routing_number_label"),
      key: "routingNo",
      val: currentPaymentMethod?.routingNumber
        ? currentPaymentMethod?.routingNumber
        : "",
      validationMessage: t("common:validation_minimum", { min: 9 }),
      extraProp: {
        required: true,
        maxLength: 9,
      },
    },
    {
      label: t("payment:accountNumber"),
      key: "accountNo",
      val: currentPaymentMethod?.accountNumber
        ? currentPaymentMethod?.accountNumber
        : "",
      validationMessage: t("common:validation_length", {
        min: 4,
        max: 17,
      }),
      extraProp: {
        required: true,
        minLength: 4,
        maxLength: 17,
      },
    },
    {
      label: t("payment_next:confirm_account_number"),
      key: "confirmAccountNo",
      val: currentPaymentMethod?.confirmAccountNo
        ? currentPaymentMethod?.confirmAccountNo
        : "",
      validationMessage: t("common:validation_length", {
        min: 4,
        max: 17,
      }),
      extraProp: {
        required: true,
        minLength: 4,
        maxLength: 17,
      },
    },
  ];

  const prepareRequestObj = useCallback(() => {
    let formIsValid = false;
    let reqData: PaymentMethod | null = null;
    if (
      formState.bankName.ref.current.checkValidity() &&
      formState.routingNo.ref.current.checkValidity() &&
      formState.accountNo.ref.current.checkValidity() &&
      formState.confirmAccountNo.ref.current.checkValidity()
    ) {
      formIsValid = true;
    } else {
      formState.bankName.ref.current.reportValidity();
      formState.routingNo.ref.current.reportValidity();
      formState.accountNo.ref.current.reportValidity();
      formState.confirmAccountNo.ref.current.reportValidity();
    }

    if (action === PaymentActionType.SAVE && ack.value !== true) {
      formIsValid = false;
    }

    if (
      action === PaymentActionType.SAVE ||
      (action === PaymentActionType.SAVE_AND_PAY_LATER && ack.value)
    ) {
      if (!nickName.value) {
        formIsValid &&= nickName.ref.current.reportValidity();
      } else {
        formIsValid &&= !isNickNameTaken;
      }
    }

    if (formIsValid) {
      reqData = {
        bankAccountType: parseInt(accountType.value),
        bankName: formState.bankName.value,
        routingNumber: formState.routingNo.value,
        accountNumber: formState.accountNo.value,
        paymentMethodName: nickName.value,
        paymentMethodType: 1,
        ack: ack.value,
      };
    }
    return reqData;
  }, [formState, accountType.value, ack, nickName, isNickNameTaken, action]);

  const submithandler = () => {
    const reqObj = prepareRequestObj();

    if (reqObj) {
      requestDispatch<SavePaymentMethod, PaymentMethod>(
        savePaymentMethod,
        reqObj,
      )
        .unwrap()
        .then((res: PaymentMethod) => {
          if (buttonClick?.count) {
            if (paymentHandler) paymentHandler(res);
          } else {
            dispatch(updateSlide({ id: "Confirmation" }));
          }
        })
        .catch((err: any) => {
          const errCode = extractError(err);
          addNotification({
            messages: errCode,
            type: "ERROR",
            closable: true,
          });
        });
    }
  };

  useEffect(() => {
    // formState.bankName check is for form initialization
    if (buttonClick?.count && formState.bankName) {
      const reqObj = prepareRequestObj();
      if (reqObj) {
        if (reqObj.ack) {
          submithandler();
        } else {
          if (paymentHandler) paymentHandler(reqObj);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buttonClick?.count]);

  const routingNo = formState.routingNo ? formState.routingNo.ref : null;

  useEffect(() => {
    if (routingNo)
      routingNo.current.validityTransform = (
        val: string,
      ): Partial<ValidityState> => {
        if (val.length === 9) {
          return {
            valid: true,
          };
        }
        return {
          valid: false,
        };
      };
  }, [routingNo]);

  const confirmAccountNo = formState.confirmAccountNo
    ? formState.confirmAccountNo.ref
    : null;

  const accountNoRef = formState.accountNo ? formState.accountNo.ref : null;

  useEffect(() => {
    if (confirmAccountNo)
      confirmAccountNo.current.validityTransform = (
        val: string,
        nativeValidity: ValidityState,
      ): Partial<ValidityState> => {
        if (!nativeValidity.valid) {
          accountNoRef.current.reportValidity();
          return {
            valid: false,
          };
        } else if (val !== accountNoRef.current.value) {
          confirmAccountNo.current.validationMessage = t(
            "payment_next:account_mismatch",
          );
          accountNoRef.current.reportValidity();
          return {
            valid: false,
          };
        }

        accountNoRef.current.reportValidity();
        return {
          valid: true,
        };
      };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmAccountNo, accountNoRef]);

  useEffect(() => {
    if (buttonClick?.scheduleFlag) {
      ack.setValue(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buttonClick?.scheduleFlag]);

  const setFormData = useCallback((key: string, val: any) => {
    setFormState((state: any) => {
      return {
        ...state,
        [key]: val,
      };
    });
  }, []);

  const renderFields = (fields: InputDTO[]) => {
    return fields.map((field: InputDTO, i) => {
      if (disabled) field.extraProp = { ...field.extraProp, disabled };
      return (
        <div key={i} className={classes.Form_Field}>
          <Input
            id={field.key}
            passState={setFormData}
            label={field.label}
            val={field.val}
            validationMessage={field.validationMessage}
            extraProp={field.extraProp}
            type={field.type}
            options={field.options}
            datacy={`add-payment-method-${field.key}`}
          />
        </div>
      );
    });
  };

  return (
    <div className={classes.ModuleWrapper}>
      {meta.isLoading ? <ft-screen-loader></ft-screen-loader> : null}
      <div className={classes.AccountType}>
        <p className={classes.AccountTypeHeading}>
          {t("payment:account_type")}
        </p>
        <ft-formfield
          class={classes.Mfa_radio_field}
          label={t("payment_next:checking")}
        >
          <ft-radio
            onClick={accountType.valueChangeHandler}
            {...renderConditionalProp(accountType.value === 1, "checked")}
            value={1}
            data-testid="add-payment-method-type-checking"
            disabled={disabled ? disabled : null}
          ></ft-radio>
        </ft-formfield>
        <ft-formfield
          class={classes.Mfa_radio_field}
          label={t("payment_next:savings")}
        >
          <ft-radio
            onClick={accountType.valueChangeHandler}
            {...renderConditionalProp(accountType.value === 2, "checked")}
            value={2}
            data-testid="add-payment-method-type-savings"
            disabled={disabled ? disabled : null}
          ></ft-radio>
        </ft-formfield>
      </div>
      <div className={classes.AccountDetail}>
        <p className={classes.AccountDetailHeading}>
          {t("payment_next:account_detail")}
        </p>
        <div
          className={
            action === PaymentActionType.PAY ? classes.PaymentFields : ""
          }
        >
          {renderFields(formData)}
          {action === PaymentActionType.SAVE ||
          (action === PaymentActionType.SAVE_AND_PAY_LATER && ack.value) ? (
            <div>
              <ft-textfield
                id="nickname"
                class={classes.NickName}
                label={t("payment_next:bank_nickname")}
                placeholder={t("payment_next:bank_nickname") + "*"}
                outlined
                required
                customInput="nickname"
                maxLength={50}
                ref={nickName.ref}
                validationMessage={t("common:field_not_empty")}
                onInput={nickName.valueChangeHandler}
                value={nickName.value}
                onBlur={handleNickOnBlur}
                icon=""
                disabled={disabled ? disabled : null}
              ></ft-textfield>
            </div>
          ) : null}
        </div>
      </div>
      <div className={classes.FormControl}>
        {action === PaymentActionType.PAY ? null : (
          <>
            <ft-formfield
              class={classes.Tnc}
              label={
                action === PaymentActionType.SAVE
                  ? t("payment_next:card_review")
                  : t("payment_next:save_method")
              }
            >
              <ft-checkbox
                ref={ack.ref}
                onClick={
                  buttonClick?.scheduleFlag || disabled
                    ? null
                    : ack.valueCheckedHandler
                }
                {...renderConditionalProp(ack.value, "checked")}
                {...renderConditionalProp(
                  buttonClick?.scheduleFlag || disabled ? true : false,
                  "disabled",
                )}
                required={true}
                validationMessage={"error"}
                data-testid="add-payment-method-type-review-and-tnc"
              ></ft-checkbox>
            </ft-formfield>
            <p
              className={classes.Confirm}
              style={{ display: disabled ? "none" : "block" }}
            >
              {action === PaymentActionType.SAVE && ack.value !== true
                ? t("payment_next:ach_save_confirm")
                : ""}
            </p>
          </>
        )}
        {action === PaymentActionType.SAVE ? (
          <div className={classes.FormButtons}>
            <ft-button
              class={[classes.Back, "outline-button"].join(" ")}
              label={t("common:back_label")}
              outlined
              onClick={() => {
                dispatch(updateSlide({ id: "ManageWallet" }));
              }}
              data-testid="add-payment-method-back"
            ></ft-button>
            <ft-button
              class={classes.Create}
              label={t("common:save_label")}
              unelevated
              onClick={submithandler}
              data-testid="add-payment-method-save"
              {...renderConditionalProp(ack.value !== true, "disabled")}
            ></ft-button>
          </div>
        ) : null}
      </div>
      <div className={classes.Example_check}>
        <ft-expansion noRipple class={classes.Expension}>
          <span slot="title">{t("payment_next:example_check")}</span>
          <div className={classes.Check_Detail}>
            <img
              alt={t("payment_next:example_check")}
              src={createAssetUrl("check_sample.svg")}
            ></img>
          </div>
        </ft-expansion>
      </div>
    </div>
  );
};
